import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  CreditCard,
  Phone,
  AccountBalance,
  Store,
  ContentCopy,
  Info,
  CheckCircle,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { PayMethodDetail } from '../../types';

interface PaymentHelpModalProps {
  open: boolean;
  onClose: () => void;
  coursePrice: number;
  onEnroll: () => void;
}

const PaymentHelpModal: React.FC<PaymentHelpModalProps> = ({
  open,
  onClose,
  coursePrice,
  onEnroll,
}) => {
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [paymentDetails, setPaymentDetails] = useState<PayMethodDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (open) {
        setLoading(true);
        setError(null);
        try {
          const details = await apiService.getPayMethodDetails();
          setPaymentDetails(details);
        } catch (err) {
          console.error('Error fetching payment details:', err);
          setError('Failed to load payment methods');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchPaymentDetails();
  }, [open]);

  const paymentMethods = [
    {
      id: 'mosan',
      name: 'Mosan',
      icon: <Phone />,
    },
    {
      id: 'tpay',
      name: 'TPay',
      icon: <CreditCard />,
    },
    {
      id: 'transfer_bank',
      name: 'Transferensia Bankaria',
      icon: <AccountBalance />,
    },
    {
      id: 'diretamente',
      name: 'Pagamentu Diretamente',
      icon: <Store />,
    },
  ];

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const selectedPaymentMethod = paymentMethods.find(method => method.id === selectedMethod);
  const activePaymentDetail = paymentDetails.length > 0 ? paymentDetails[0] : null;

  interface PaymentInfo {
    id?: string;
    qr?: string;
    accountName?: string;
    ibanBank?: string;
    address?: string;
    hours?: string;
    contact?: string;
    instructions: string[];
    agentInstructions?: string[];
  }

  const getPaymentInfo = (): PaymentInfo | null => {
    if (!selectedPaymentMethod || !activePaymentDetail) return null;

    switch (selectedPaymentMethod.id) {
      case 'mosan':
        return {
          id: activePaymentDetail.mosan_no,
          qr: activePaymentDetail.mosan_qr,
          instructions: [
            'Loke Mosan app',
            'Hili "Transfere"',
            'Hatama numeru Mosan iha leten',
            `Hatama montante osan tuir durasaun subskrisaun`,
            'Konfirma no halo pagamentu',
          ],
          agentInstructions: [
            'Vizita Agente Mosan ne\'ebe besik',
            'Fó numeru Mosan iha leten ba agente',
            `Fó montante $${coursePrice} (osan cash) ba agente`,
            'Agente sei halo transferensia ba numeru ne\'e',
            'Hetan resibu ka evidensia pagamentu husi agente',
          ],
        };
      case 'tpay':
        return {
          id: activePaymentDetail.tpay_no,
          qr: activePaymentDetail.tpay_qr,
          instructions: [
            'Loke TPay app',
            'Hili "Transfer"',
            'Hili "Transfer ba TPay"',
            'Prense Numeru Telefone / Konta TPay iha leten',
            `Hatama montante osan tuir durasaun subskrisaun`,
            'Konfirma no halo pagamentu',
          ],
        };
      case 'transfer_bank':
        return {
          id: activePaymentDetail.bank_account_no ?? undefined,
          accountName: activePaymentDetail.bank_behalf ?? undefined,
          ibanBank: activePaymentDetail.iban_bank ?? undefined,
          instructions: [
            'Ba banku ka uza internet banking',
            'Halo transferensia ba konta iha leten',
            `Hatama montante osan tuir durasaun subskrisaun`,
            'Konfirma no halo pagamentu',
          ],
        };
      case 'diretamente':
        return {
          address: 'Rua Tv. de Lecidere, Dili',
          hours: '08:00 - 17:00',
          contact: '+670 74190343',
          instructions: [
            'Ba enderesu iha leten',
            'Halo pagamentu diretamente',
            'Hetan receipt ka evidensia',
          ],
        };
      default:
        return null;
    }
  };

  const paymentInfo = getPaymentInfo();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '95vh',
          overflow: 'auto',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Oinsá atu selu?
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2, overflow: 'visible' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* Payment Method Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ color: 'text.primary' }}>Hili Metodu Pagamentu</InputLabel>
              <Select
                value={selectedMethod}
                label="Hili Metodu Pagamentu"
                onChange={(e) => setSelectedMethod(e.target.value)}
                sx={{
                  '& .MuiSelect-select': {
                    color: 'text.primary',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#10b981',
                  },
                }}
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method.id} value={method.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {method.icon}
                      <Typography sx={{ color: 'text.primary' }}>{method.name}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Payment Details */}
            {selectedPaymentMethod && paymentInfo && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                  {selectedPaymentMethod.name}
                </Typography>

                {/* Payment ID/Details */}
                {(paymentInfo.id || selectedPaymentMethod.id === 'transfer_bank') && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                      {selectedPaymentMethod.id === 'transfer_bank' ? 'Detalla Konta Bankaria:' : `${selectedPaymentMethod.name} ID:`}
                    </Typography>
                    {paymentInfo.id && (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                        <TextField
                          value={paymentInfo.id}
                          InputProps={{ readOnly: true }}
                          size="small"
                          sx={{ 
                            flexGrow: 1,
                            '& .MuiInputBase-input': { color: 'text.primary' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                          }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ContentCopy />}
                          onClick={() => paymentInfo.id && copyToClipboard(paymentInfo.id)}
                          sx={{
                            borderColor: '#10b981',
                            color: '#10b981',
                            '&:hover': { borderColor: '#059669', backgroundColor: 'rgba(16, 185, 129, 0.1)' },
                          }}
                        >
                          Kopía
                        </Button>
                      </Box>
                    )}
                    {selectedPaymentMethod.id === 'transfer_bank' && (
                      <>
                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                          Naran: {paymentInfo.accountName || '—'},
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                          Banku: BNCTL
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.primary' }}>
                            IBAN:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              value={paymentInfo.ibanBank || '—'}
                              InputProps={{ readOnly: true }}
                              size="small"
                              sx={{
                                flexGrow: 1,
                                '& .MuiInputBase-input': { color: 'text.primary' },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                              }}
                            />
                            {paymentInfo.ibanBank && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ContentCopy />}
                                onClick={() => copyToClipboard(paymentInfo.ibanBank!)}
                                sx={{
                                  borderColor: '#10b981',
                                  color: '#10b981',
                                  '&:hover': { borderColor: '#059669', backgroundColor: 'rgba(16, 185, 129, 0.1)' },
                                }}
                              >
                                Kopía
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                )}

                {/* QR Code Display */}
                {paymentInfo.qr && (
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                      Scan QR Code:
                    </Typography>
                    <Box
                      component="img"
                      src={paymentInfo.qr}
                      alt={`${selectedPaymentMethod.name} QR Code`}
                      sx={{
                        maxWidth: '200px',
                        width: '100%',
                        height: 'auto',
                        border: '2px solid rgba(255, 255, 255, 0.23)',
                        borderRadius: 1,
                        p: 1,
                        backgroundColor: 'white',
                      }}
                    />
                  </Box>
                )}

                {/* Direct Payment Details */}
                {selectedPaymentMethod.id === 'diretamente' && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
                      <strong>Enderesu:</strong> {paymentInfo.address}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
                      <strong>Oras servisu:</strong> {paymentInfo.hours}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
                      <strong>Kontaktu:</strong> {paymentInfo.contact}
                    </Typography>
                  </Box>
                )}

                {/* Instructions (app) */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                    {paymentInfo.agentInstructions?.length ? 'Instrusaun (via app):' : 'Instrusaun:'}
                  </Typography>
                  {paymentInfo.instructions.map((instruction, index) => (
                    <Typography key={index} variant="body2" sx={{ mb: 0.5, pl: 1, color: 'text.primary' }}>
                      {index + 1}. {instruction}
                    </Typography>
                  ))}
                </Box>

                {/* Agent instructions (Mosan) */}
                {paymentInfo.agentInstructions && paymentInfo.agentInstructions.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                      Instrusaun (via agent Mosan):
                    </Typography>
                    {paymentInfo.agentInstructions.map((instruction, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 0.5, pl: 1, color: 'text.primary' }}>
                        {index + 1}. {instruction}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Important Note */}
            <Box sx={{ p: 2, backgroundColor: 'rgba(16, 185, 129, 0.15)', borderRadius: 1, border: '1px solid rgba(16, 185, 129, 0.3)' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                Nota Importante:
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5, color: 'text.primary' }}>
                • Favor screenshot/hasai foto evidensia pagamentu (resibu)
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5, color: 'text.primary' }}>
                • Klik butaun 'Rejista' iha okos
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5, color: 'text.primary' }}>
                • Prense ita-boot nia e-mail, hili metodu pagamentu ne'ebe ita-boot uza, prense subskripsaun ba fulan hira no upload evidensia pagamentu (resibu) ne'ebe mak iha. 
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                • Klik Butaun Submit Purchase
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{
            borderColor: '#10b981',
            color: '#10b981',
            '&:hover': {
              borderColor: '#059669',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
            },
          }}
        >
          Taka
        </Button>
        <Button 
          variant="contained"
          onClick={() => {
            onClose();
            onEnroll();
          }}
          sx={{
            backgroundColor: '#10b981',
            '&:hover': {
              backgroundColor: '#059669',
            },
          }}
        >
          Rejista
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentHelpModal;
